import { z } from 'zod';

// ─── Evidence & Score Primitives ────────────────────────────────────────────

export const EvidenceSpanSchema = z.object({
  source: z.enum(['message', 'reaction', 'personaProfile']).catch('message'),
  quote: z.string().catch(''),
  impact: z.enum(['positive', 'negative', 'neutral']).catch('neutral'),
});
export type EvidenceSpan = z.infer<typeof EvidenceSpanSchema>;

export const ScoreSchema = z.object({
  value: z.number().min(0).max(100),
  confidence: z.number().min(0).max(1),
  reasoning: z.string(),
  evidence: z.array(EvidenceSpanSchema).catch([]),
  personaAttributesUsed: z.array(z.string()).catch([]),
});
export type Score = z.infer<typeof ScoreSchema>;

// ─── Input Schemas ──────────────────────────────────────────────────────────

/** Maximum number of messages that can be tested in a single run. */
export const MAX_TESTING_MESSAGES = 10;

export const MessageTestingMessageSchema = z.object({
  id: z.string(),
  code: z.string().optional().default(''),
  title: z.string().optional().default(''),
  value: z.string(),
  category: z.string(),
});
export type MessageTestingMessage = z.infer<typeof MessageTestingMessageSchema>;

export const MessageTestingRequestSchema = z.object({
  segmentIdentifiers: z.array(z.string()).min(1),
  messages: z.array(MessageTestingMessageSchema).min(1),
  brand: z.string(),
  objective: z.string().optional(),
  segmentationRunId: z.number().int().positive().optional(),
});
export type MessageTestingRequest = z.infer<typeof MessageTestingRequestSchema>;

// ─── Run Inputs Response ────────────────────────────────────────────────────

export interface MessageTestingRunInputs {
  id: number;
  status: string;
  messages: Array<{
    id: string;
    code: string;
    displayTitle: string;
    body: string;
    category: string;
    brand: string;
  }>;
  segments: Array<{
    identifier: string;
    name: string;
  }>;
  brand: string;
  objective?: string;
  segmentationRun: {
    id: number;
    runKey: string;
    projectTitle: string | null;
  } | null;
  savedToDashboard: boolean;
}

// ─── Step 1: Reaction Transcript ────────────────────────────────────────────

export const ReactionArcPhaseSchema = z.object({
  phase: z.enum(['opener', 'middle', 'close']).catch('middle'),
  sentiment: z
    .enum(['positive', 'neutral', 'negative', 'mixed', 'measured', 'skeptical', 'cautious'])
    .catch('neutral'),
  summary: z.string().catch(''),
});

export const ReactionTranscriptSchema = z.object({
  messageId: z.string(),
  segmentIdentifier: z.string(),
  transcript: z.string(),
  personaAttributesInvoked: z.array(z.string()).catch([]),
  reactionArc: z.array(ReactionArcPhaseSchema).catch([]),
});
export type ReactionTranscript = z.infer<typeof ReactionTranscriptSchema>;

// ─── Step 2: Message Testing Cell ───────────────────────────────────────────

export const ImprovementSuggestionSchema = z.object({
  change: z.string(),
  rationale: z.string(),
  predictedScoreLift: z.enum(['low', 'medium', 'high']).catch('medium'),
  scoresAffected: z.array(z.string()).catch([]),
});

export const OverallAssessmentSchema = z.object({
  verdict: z.enum(['strong_fit', 'workable', 'weak_fit', 'do_not_use']).catch('workable'),
  oneLineSummary: z.string(),
  topStrength: z.string().catch(''),
  topWeakness: z.string().catch(''),
});

export const VerdictSchema = OverallAssessmentSchema.shape.verdict;
export type Verdict = z.infer<typeof VerdictSchema>;

/**
 * The five core scoring dimensions for message testing:
 * - clarity: Does the target audience take away the intended message?
 * - relevance: Is the content meaningful to the target audience?
 * - differentiating: Does the message stand out compared to traditional campaigns?
 * - motivating: Does the message motivate respondents to act or change (to prescribe)?
 * - credibility: Does the message convey trust and build HCP confidence in the brand?
 */
export const MessageTestingScoresSchema = z.object({
  clarity: ScoreSchema,
  relevance: ScoreSchema,
  differentiating: ScoreSchema,
  motivating: ScoreSchema,
  credibility: ScoreSchema,
});

// ─── Judge Output Schema ────────────────────────────────────────────────────

export const ScoresJudgeOutputSchema = z.object({
  scores: MessageTestingScoresSchema,
  improvementSuggestions: z.array(ImprovementSuggestionSchema),
  overallAssessment: OverallAssessmentSchema,
});
export type ScoresJudgeOutput = z.infer<typeof ScoresJudgeOutputSchema>;

// ─── Composite Cell ─────────────────────────────────────────────────────────

export const MessageTestingCellSchema = z.object({
  messageId: z.string(),
  segmentId: z.string(),
  generatedAt: z.string(),

  scores: MessageTestingScoresSchema,

  improvementSuggestions: z.array(ImprovementSuggestionSchema),
  overallAssessment: OverallAssessmentSchema,
});
export type MessageTestingCell = z.infer<typeof MessageTestingCellSchema>;

// ─── Rollup Schemas ─────────────────────────────────────────────────────────

const RollupRecommendationSchema = z.object({
  action: z.string(),
  rationale: z.string(),
  priority: z.enum(['high', 'medium', 'low']),
});

const RollupMessageReferenceSchema = z.object({
  messageId: z.string(),
  why: z.string(),
});

const DimensionInsightSchema = z.object({
  dimension: z.enum(['clarity', 'relevance', 'differentiating', 'motivating', 'credibility']),
  assessment: z.string(),
  verdict: VerdictSchema,
});

export const SegmentRollupSchema = z.object({
  segmentIdentifier: z.string(),
  segmentName: z.string(),
  executiveSummary: z.string(),
  dimensionInsights: z.array(DimensionInsightSchema),
  keyStrengths: z.array(z.string()),
  keyWeaknesses: z.array(z.string()),
  categoryPerformanceRanking: z.array(
    z.object({
      category: z.string(),
      meanComposite: z.number(),
      verdict: VerdictSchema,
    })
  ),
  recommendations: z.array(RollupRecommendationSchema),
});
export type SegmentRollup = z.infer<typeof SegmentRollupSchema>;

export const CategoryRollupSchema = z.object({
  category: z.string(),
  executiveSummary: z.string(),
  crossPersonaAnalysis: z.string(),
  dimensionInsights: z.array(DimensionInsightSchema),
  bestMessages: z.array(RollupMessageReferenceSchema),
  worstMessages: z.array(RollupMessageReferenceSchema),
  personaInsights: z.array(
    z.object({
      segmentIdentifier: z.string(),
      segmentName: z.string(),
      fitAssessment: z.string(),
    })
  ),
  recommendations: z.array(RollupRecommendationSchema),
});
export type CategoryRollup = z.infer<typeof CategoryRollupSchema>;

export const MatrixRollupSchema = z.object({
  executiveSummary: z.string(),
  portfolioHealthScore: z.number().min(0).max(100),
  dimensionInsights: z.array(DimensionInsightSchema),
  universalWinners: z.array(RollupMessageReferenceSchema),
  universalLosers: z.array(RollupMessageReferenceSchema),
  crossSegmentPatterns: z.array(z.string()),
  crossCategoryPatterns: z.array(z.string()),
  strategicRecommendations: z.array(
    RollupRecommendationSchema.extend({
      impactEstimate: z.string(),
    })
  ),
  priorityActionItems: z.array(
    z.object({
      action: z.string(),
      urgency: z.enum(['immediate', 'short_term', 'long_term']),
    })
  ),
});
export type MatrixRollup = z.infer<typeof MatrixRollupSchema>;

export const MessageTestingRollupsSchema = z.object({
  segmentRollups: z.array(SegmentRollupSchema),
  categoryRollups: z.array(CategoryRollupSchema),
  matrixRollup: MatrixRollupSchema,
});
export type MessageTestingRollups = z.infer<typeof MessageTestingRollupsSchema>;

// ─── Result Envelope ────────────────────────────────────────────────────────

export const MessageTestingProgressSchema = z.object({
  runId: z.number(),
  completed: z.number(),
  total: z.number(),
  latestCell: MessageTestingCellSchema.optional(),
});
export type MessageTestingProgress = z.infer<typeof MessageTestingProgressSchema>;

export const MessageTestingResultSchema = z.object({
  runId: z.number(),
  status: z.enum(['processing', 'analyzing', 'completed', 'failed']),
  matrix: z.array(MessageTestingCellSchema),
  progress: z.object({
    completed: z.number(),
    total: z.number(),
  }),
  rollups: MessageTestingRollupsSchema.optional(),
});
export type MessageTestingResult = z.infer<typeof MessageTestingResultSchema>;

// ─── Message Testing State ──────────────────────────────────────────────────

export const MessageTestingPhaseSchema = z.enum(['idle', 'configuring', 'running', 'completed', 'error']);
export type MessageTestingPhase = z.infer<typeof MessageTestingPhaseSchema>;

export const MessageTestingStateSchema = z.object({
  phase: MessageTestingPhaseSchema.default('idle'),
  /** Messages to test — typically sourced from message generation output. */
  messages: z.array(MessageTestingMessageSchema).default([]),
  /** Segment identifiers whose personas will be used for simulation. */
  segmentIdentifiers: z.array(z.string()).default([]),
  /** Brand context for the testing simulation. */
  brand: z.string().default('Elderberry'),
  /** Optional objective for signal extraction scoring. */
  objective: z.string().nullable().default(null),
  /** Numeric id of the current / most recent BullMQ run. */
  runId: z.number().nullable().default(null),
  /** Accumulated result cells from the testing matrix. */
  results: z.array(MessageTestingCellSchema).default([]),
  /** Live progress tracking. */
  progress: z
    .object({
      completed: z.number(),
      total: z.number(),
    })
    .nullable()
    .default(null),
  /** Error message if the testing run failed. */
  error: z.string().nullable().default(null),
});
export type MessageTestingState = z.infer<typeof MessageTestingStateSchema>;

/** Default initial state for message testing. */
export function getDefaultMessageTestingState(): MessageTestingState {
  return MessageTestingStateSchema.parse({});
}

// ─── Message Testing Actions ────────────────────────────────────────────────

export const MessageTestingActionType = {
  SetTestingDetails: 'SET_TESTING_DETAILS',
  StartTesting: 'START_TESTING',
  SetTestingProgress: 'SET_TESTING_PROGRESS',
  SetTestingResults: 'SET_TESTING_RESULTS',
  SetTestingError: 'SET_TESTING_ERROR',
  ResetTesting: 'RESET_TESTING',
} as const;

export type MessageTestingActionType =
  (typeof MessageTestingActionType)[keyof typeof MessageTestingActionType];

export type MessageTestingAction =
  | {
      type: typeof MessageTestingActionType.SetTestingDetails;
      payload: {
        messages?: MessageTestingMessage[];
        segmentIdentifiers?: string[];
        brand?: string;
        objective?: string | null;
      };
    }
  | {
      type: typeof MessageTestingActionType.StartTesting;
      payload: {
        runId: number;
        totalPairs: number;
      };
    }
  | {
      type: typeof MessageTestingActionType.SetTestingProgress;
      payload: {
        completed: number;
        total: number;
        latestCell?: MessageTestingCell;
      };
    }
  | {
      type: typeof MessageTestingActionType.SetTestingResults;
      payload: {
        matrix: MessageTestingCell[];
      };
    }
  | {
      type: typeof MessageTestingActionType.SetTestingError;
      payload: { error: string };
    }
  | {
      type: typeof MessageTestingActionType.ResetTesting;
    };

// ─── Message Testing Reducer ────────────────────────────────────────────────

/**
 * Pure reducer for MessageTestingState.
 * Single source of truth for all testing state mutations — used by both
 * backend (session persistence) and frontend (optimistic updates).
 */
export function messageTestingReducer(
  state: MessageTestingState,
  action: MessageTestingAction
): MessageTestingState {
  switch (action.type) {
    case MessageTestingActionType.SetTestingDetails: {
      const { messages, segmentIdentifiers, brand, objective } = action.payload;
      const next = { ...state };
      if (messages !== undefined) next.messages = messages;
      if (segmentIdentifiers !== undefined) next.segmentIdentifiers = segmentIdentifiers;
      if (brand !== undefined) next.brand = brand;
      if (objective !== undefined) next.objective = objective;
      if (next.phase === 'idle') next.phase = 'configuring';
      return next;
    }

    case MessageTestingActionType.StartTesting:
      return {
        ...state,
        phase: 'running',
        runId: action.payload.runId,
        progress: { completed: 0, total: action.payload.totalPairs },
        results: [],
        error: null,
      };

    case MessageTestingActionType.SetTestingProgress: {
      const { completed, total, latestCell } = action.payload;
      return {
        ...state,
        progress: { completed, total },
        results: latestCell ? [...state.results, latestCell] : state.results,
      };
    }

    case MessageTestingActionType.SetTestingResults:
      return {
        ...state,
        phase: 'completed',
        results: action.payload.matrix,
        progress: state.progress
          ? { ...state.progress, completed: action.payload.matrix.length }
          : { completed: action.payload.matrix.length, total: action.payload.matrix.length },
      };

    case MessageTestingActionType.SetTestingError:
      return {
        ...state,
        phase: 'error',
        error: action.payload.error,
      };

    case MessageTestingActionType.ResetTesting:
      return getDefaultMessageTestingState();

    default:
      return state;
  }
}

/**
 * Apply a sequence of testing actions to a state, returning the final state.
 */
export function applyMessageTestingActions(
  state: MessageTestingState,
  actions: MessageTestingAction[]
): MessageTestingState {
  return actions.reduce(messageTestingReducer, state);
}
